import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import api from '../lib/api';
import { isAxiosError } from 'axios';

export interface OnboardingPreference {
  status: 'skipped' | 'completed';
  completedDate?: string; // ISO 8601 timestamp
}

export interface OnboardingPreferenceResponse {
  data: OnboardingPreference | null;
}

export interface UpdateOnboardingPreferenceRequest {
  status: 'skipped' | 'completed';
  completedDate?: string;
}

// Hook to get onboarding preference
export const useOnboardingPreference = (
  options?: UseQueryOptions<OnboardingPreference | null, Error>,
) => {
  return useQuery({
    queryKey: ['onboarding-preference'],
    queryFn: async (): Promise<OnboardingPreference | null> => {
      try {
        const response = await api.get<OnboardingPreferenceResponse>(
          '/api/core/pleri/preferences/user/onboarding',
        );
        return response.data.data;
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Hook to update onboarding preference
export const useUpdateOnboardingPreference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: UpdateOnboardingPreferenceRequest,
    ): Promise<OnboardingPreference> => {
      const response = await api.put<OnboardingPreferenceResponse>(
        '/api/core/pleri/preferences/user/onboarding',
        params,
      );
      await queryClient.refetchQueries({ queryKey: ['pleriStatus'] });
      if (!response.data.data) {
        throw new Error('Failed to update onboarding preference');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-preference'] });
    },
  });
};
